import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Auth0Provider, useAuth0 } from '@auth0/auth0-react';
import { clearTokens } from './tokenStorage';

type AuthContextValue = {
  initialized: boolean;
  authenticated: boolean;
  login: (options?: { redirectUri?: string }) => Promise<void>;
  logout: () => Promise<void>;
  getToken: () => Promise<string | undefined>;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

function readConfig() {
  const domain = (process.env.REACT_APP_AUTH0_DOMAIN || (window as any).REACT_APP_AUTH0_DOMAIN || '').trim();
  const clientId = (process.env.REACT_APP_AUTH0_CLIENT_ID || (window as any).REACT_APP_AUTH0_CLIENT_ID || '').trim();
  const audience = (process.env.REACT_APP_AUTH0_AUDIENCE || (window as any).REACT_APP_AUTH0_AUDIENCE || '').trim();

  if (!domain || !clientId) {
    // eslint-disable-next-line no-console
    console.error('Missing Auth0 config. Provide REACT_APP_AUTH0_DOMAIN and REACT_APP_AUTH0_CLIENT_ID.');
  }

  return { domain, clientId, audience };
}

/**
 * Inner component that exposes Auth0 state through the same
 * AuthContextValue interface previously used by Keycloak, so all
 * consuming components keep working without changes.
 */
const AuthBridge: React.FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  const {
    isLoading,
    isAuthenticated,
    loginWithRedirect,
    logout: auth0Logout,
    getAccessTokenSilently,
  } = useAuth0();

  const initialized = !isLoading;
  const authenticated = isAuthenticated;

  const login = useCallback(
    async (options?: { redirectUri?: string }) => {
      await loginWithRedirect({
        appState: { returnTo: options?.redirectUri || window.location.pathname },
      });
    },
    [loginWithRedirect]
  );

  const logout = useCallback(async () => {
    clearTokens();
    await auth0Logout({
      logoutParams: { returnTo: `${window.location.origin}/login` },
    });
  }, [auth0Logout]);

  const getToken = useCallback(async () => {
    try {
      return await getAccessTokenSilently();
    } catch {
      // Token could not be obtained (user not authenticated or consent needed)
      return undefined;
    }
  }, [getAccessTokenSilently]);

  const value = useMemo<AuthContextValue>(
    () => ({ initialized, authenticated, login, logout, getToken }),
    [initialized, authenticated, login, logout, getToken]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const AuthProvider: React.FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  const { domain, clientId, audience } = useMemo(() => readConfig(), []);

  if (!domain || !clientId) {
    return <>{children}</>;
  }

  return (
    <Auth0Provider
      domain={domain}
      clientId={clientId}
      authorizationParams={{
        redirect_uri: `${window.location.origin}/callback`,
        audience: audience || undefined,
      }}
      cacheLocation="localstorage"
      useRefreshTokens
    >
      <AuthBridge>{children}</AuthBridge>
    </Auth0Provider>
  );
};

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
